import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { ALL_COUNTRIES, joinCountries, splitCountries } from "@/lib/countries";

const useCatalogCountries = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/catalog/countries").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return list;
};

function Picker({ testId, options, selected, onToggle, placeholder, summary, multi, onClear }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  // keep values imported from HubSpot even if they are not in the list
  const all = useMemo(() => [...new Set([...selected, ...options])], [selected, options]);
  const shown = all.filter((c) => c.toLowerCase().includes(q.trim().toLowerCase()));
  const pick = (c) => { onToggle(c); if (!multi) setOpen(false); };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className="mt-1.5 w-full min-h-11 px-3 py-2 rounded-xl border border-input bg-white text-left text-sm flex items-center gap-2 hover:border-navy/40 transition-colors">
          <span className={`flex-1 truncate ${selected.length ? "text-navy" : "text-muted-foreground"}`}>{selected.length ? summary : placeholder}</span>
          {selected.length > 0 && (
            <span role="button" tabIndex={0} aria-label="Clear" data-testid={`${testId}-clear`} onClick={(e) => { e.stopPropagation(); onClear(); }} className="p-0.5 rounded hover:bg-secondary"><X className="h-3.5 w-3.5 text-muted-foreground" /></span>
          )}
          <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-64 p-2" data-testid={`${testId}-panel`}>
        <div className="flex items-center gap-2 px-2 h-9 mb-1 rounded-lg bg-secondary">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a country" data-testid={`${testId}-search`} className="flex-1 bg-transparent text-sm outline-none" />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {shown.map((c) => {
            const on = selected.includes(c);
            return (
              <button type="button" key={c} onClick={() => pick(c)} data-testid={`${testId}-option-${c}`} className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm text-left hover:bg-secondary ${on ? "font-semibold text-navy" : "text-navy/80"}`}>
                <span className={`h-4 w-4 shrink-0 flex items-center justify-center border ${multi ? "rounded" : "rounded-full"} ${on ? "bg-orange border-orange text-white" : "border-border"}`}>{on && <Check className="h-3 w-3" />}</span>
                <span className="flex-1 truncate">{c}</span>
              </button>
            );
          })}
          {!shown.length && <p className="px-2 py-3 text-xs text-muted-foreground">No country found</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Study destinations: none, one or several countries of the Elite catalog. */
export function TargetCountriesPicker({ value, onChange, testId = "target-countries" }) {
  const options = useCatalogCountries();
  const selected = splitCountries(value);
  const toggle = (c) => onChange(joinCountries(selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c]));
  const summary = selected.length > 2 ? `${selected.slice(0, 2).join(", ")} +${selected.length - 2}` : selected.join(", ");
  return <Picker multi testId={testId} options={options} selected={selected} onToggle={toggle} onClear={() => onChange("")} placeholder="Any country (optional)" summary={summary} />;
}

/** Country of residence: a single country. */
export function CountrySelect({ value, onChange, testId = "residence-country" }) {
  const selected = value ? [value] : [];
  return <Picker testId={testId} options={ALL_COUNTRIES} selected={selected} onToggle={(c) => onChange(c === value ? "" : c)} onClear={() => onChange("")} placeholder="Select your country" summary={value} />;
}
